import React, { useState, useCallback, useEffect, useRef } from "react";
import { Play, Loader2, Eye, EyeOff, Copy, Check, Clock, Upload } from "lucide-react";
import { API_BASE } from "../../data/apiDocsData";

function pathParamDefs(endpoint) {
  if (endpoint?.pathParams?.length) return endpoint.pathParams;
  const names = [...(endpoint?.path?.matchAll(/\{([^}]+)\}/g) || [])].map((m) => m[1]);
  return names.map((name) => ({ name, required: true }));
}

function applyPathParams(path, values) {
  return path.replace(/\{([^}]+)\}/g, (_, name) => {
    const v = values[name];
    return v != null && String(v).trim() !== "" ? encodeURIComponent(String(v).trim()) : `{${name}}`;
  });
}

const ApiPlayground = ({ endpoint }) => {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [body, setBody] = useState("");
  const [response, setResponse] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formFields, setFormFields] = useState({});
  const [pathValues, setPathValues] = useState({});
  const fileInputRef = useRef(null);

  const params = pathParamDefs(endpoint);

  useEffect(() => {
    if (!endpoint) return;
    const next = {};
    pathParamDefs(endpoint).forEach((p) => {
      next[p.name] = "";
    });
    setPathValues(next);
    if (endpoint.isMultipart) {
      const fields = {};
      endpoint.requestFields?.filter((f) => f.type !== "file").forEach((f) => {
        fields[f.name] = "";
      });
      setFormFields(fields);
      setSelectedFile(null);
    } else if (endpoint.requestBody) {
      setBody(JSON.stringify(endpoint.requestBody, null, 2));
    } else {
      setBody("");
    }
    setResponse(null);
    setStatusCode(null);
    setResponseTime(null);
  }, [endpoint]);

  const resolvedPath = endpoint ? applyPathParams(endpoint.path, pathValues) : "";
  const missingPath = params.filter((p) => p.required !== false && !String(pathValues[p.name] || "").trim());

  const handleSend = useCallback(async () => {
    if (!endpoint || !apiKey.trim()) return;
    if (missingPath.length) {
      setStatusCode(400);
      setResponseTime(0);
      setResponse(JSON.stringify({
        success: false,
        error: {
          code: "INVALID_PAYLOAD",
          message: `Path parameter required: ${missingPath.map((p) => p.name).join(", ")}`,
        },
      }, null, 2));
      return;
    }
    setLoading(true);
    setResponse(null);
    setStatusCode(null);

    const start = performance.now();

    try {
      let url = `${API_BASE}${applyPathParams(endpoint.path, pathValues)}`;
      const headers = { "X-API-Key": apiKey };
      let fetchOptions = { method: endpoint.method, headers };

      if (endpoint.method === "GET") {
        if (body.trim()) {
          try {
            const query = JSON.parse(body);
            const qs = new URLSearchParams(query).toString();
            if (qs) url += `?${qs}`;
          } catch { /* ignore */ }
        }
      } else if (endpoint.isMultipart) {
        const form = new FormData();
        Object.entries(formFields).forEach(([k, v]) => {
          if (v) form.append(k, v);
        });
        if (selectedFile) form.append("file", selectedFile);
        fetchOptions.body = form;
      } else {
        headers["Content-Type"] = "application/json";
        fetchOptions.headers = headers;
        fetchOptions.body = body;
      }

      fetchOptions.headers = headers;
      const res = await fetch(url, fetchOptions);
      const elapsed = Math.round(performance.now() - start);
      const data = await res.json();

      setStatusCode(res.status);
      setResponseTime(elapsed);
      setResponse(JSON.stringify(data, null, 2));
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      setResponseTime(elapsed);
      setStatusCode(0);
      setResponse(JSON.stringify({ error: "Network error", message: err.message }, null, 2));
    } finally {
      setLoading(false);
    }
  }, [endpoint, apiKey, body, formFields, selectedFile, pathValues, missingPath]);

  const handleCopyResponse = useCallback(() => {
    if (!response) return;
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [response]);

  if (!endpoint) {
    return (
      <div className="apidocs-play-empty">
        Select an API endpoint to try it here
      </div>
    );
  }

  return (
    <div className="apidocs-play-form">
      <div className="apidocs-play-meta">
        <div className="apidocs-ep-path">
          <span className={`apidocs-method ${(endpoint.method || "").toLowerCase()}`}>
            {endpoint.method}
          </span>
          <code>{resolvedPath}</code>
        </div>

        {params.length > 0 && (
          <div className="apidocs-path-params">
            {params.map((p) => (
              <label key={p.name}>
                {p.name}
                <input
                  type="text"
                  value={pathValues[p.name] || ""}
                  onChange={(e) => setPathValues((prev) => ({ ...prev, [p.name]: e.target.value }))}
                  placeholder={p.description || p.name}
                />
              </label>
            ))}
          </div>
        )}

        <div className="apidocs-key">
          <input
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="pk_live_your_api_key"
          />
          <button type="button" onClick={() => setShowKey(!showKey)}>
            {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        </div>
      </div>

      <div className="apidocs-play-fields">
        <label>
          {endpoint.isMultipart ? "Form fields" : endpoint.method === "GET" ? "Query parameters (JSON)" : "Request body"}
        </label>

        {endpoint.isMultipart ? (
          <div className="apidocs-mp">
            {Object.keys(formFields).map((key) => (
              <div key={key}>
                <label>{key}</label>
                <input
                  type="text"
                  value={formFields[key]}
                  onChange={(e) => setFormFields((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
            ))}
            <div>
              <label>file</label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              {selectedFile && (
                <div className="apidocs-file">
                  <Upload size={10} /> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>
          </div>
        ) : (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            spellCheck={false}
          />
        )}

        <button
          type="button"
          onClick={handleSend}
          disabled={loading || !apiKey.trim()}
          className="ping-btn ping-btn-primary disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {loading ? "Sending…" : "Send request"}
        </button>

        {response && (
          <div>
            <div className="apidocs-block-head">
              <div className="apidocs-res-meta">
                <span>Response</span>
                {statusCode !== null && (
                  <span className={`ping-chip ${statusCode >= 200 && statusCode < 300 ? "g" : "r"}`}>
                    {statusCode || "ERR"}
                  </span>
                )}
                {responseTime !== null && (
                  <span className="apidocs-ms">
                    <Clock size={10} /> {responseTime}ms
                  </span>
                )}
              </div>
              <button type="button" onClick={handleCopyResponse}>
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
            <pre className="apidocs-pre">{response}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiPlayground;
